import {
  Control,
  Controller,
  FieldErrors,
  useFieldArray,
  UseFormWatch,
} from 'react-hook-form';
import { MouseEvent } from 'react';
import { UserFormData } from '../../add-user-dialog/user-form-page';

import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import SharedProfileInformationAccordion from '../../../../shared/shared-profile-information-accordion';
import SharedProfileInformationItemActions from '../../../../shared/shared-profile-information-item-actions';

interface PhonesAccordionProps {
  control: Control<UserFormData>;
  errors: FieldErrors<UserFormData>;
  watch: UseFormWatch<UserFormData>;
  disabled: boolean;
}

export default function PhonesAccordion({
  control,
  errors,
  disabled,
  watch,
}: PhonesAccordionProps) {
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'phones',
  });

  const menuItems = [
    { value: 'assistant', label: 'Assistant' },
    { value: 'callback', label: 'Callback' },
    { value: 'car', label: 'Car' },
    { value: 'company_main', label: 'Company Main' },
    { value: 'custom', label: 'Custom' },
    { value: 'grand_central', label: 'Google Voice' },
    { value: 'home', label: 'Home' },
    { value: 'home_fax', label: 'Home Fax' },
    { value: 'isdn', label: 'ISDN' },
    { value: 'main', label: 'Main' },
    { value: 'mobile', label: 'Mobile' },
    { value: 'other', label: 'Other' },
    { value: 'other_fax', label: 'Other Fax' },
    { value: 'pager', label: 'Pager' },
    { value: 'radio', label: 'Radio' },
    { value: 'telex', label: 'Telex' },
    { value: 'tty_tdd', label: 'TTY TDD' },
    { value: 'work', label: 'Work' },
    { value: 'work_fax', label: 'Work Fax' },
    { value: 'work_mobile', label: 'Work Mobile' },
    { value: 'work_pager', label: 'Work Pager' },
  ];

  const phonesErrors = errors['phones'];
  const phonesCount = fields.length;

  const handleOnAdd = (
    event: MouseEvent<HTMLSpanElement, globalThis.MouseEvent>
  ) => {
    event.stopPropagation();
    append({
      type: '',
      primary: false,
      customType: '',
      value: '',
    });
  };

  return (
    <SharedProfileInformationAccordion
      title="Phones"
      count={phonesCount}
      hasError={!!phonesErrors}
      onAdd={handleOnAdd}
      disabled={disabled}
    >
      {fields.map((item, index) => {
        const typeField = watch(`phones.${index}.type`);

        return (
          <Box key={item.id}>
            <Stack
              gap={1}
              flexDirection="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ my: 1.5 }}
            >
              <Stack flexDirection="row" alignItems="center" gap={2}>
                <Controller
                  rules={{ required: 'This field is required.' }}
                  name={`phones.${index}.type`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required
                      inputRef={field.ref}
                      inputProps={{
                        id: `phones-type-${index}`,
                      }}
                      InputLabelProps={{
                        htmlFor: `phones-type-${index}`,
                      }}
                      name="phones"
                      select
                      disabled={disabled}
                      label="Type"
                      error={!!phonesErrors?.[index]?.type}
                      helperText={
                        phonesErrors?.[index]?.type
                          ? 'This field is required.'
                          : ''
                      }
                      sx={{
                        minWidth: 150,
                      }}
                    >
                      {menuItems.map((item) => (
                        <MenuItem value={item.value} key={item.label}>
                          {item.label}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
                {typeField === 'custom' && (
                  <Controller
                    rules={{ required: 'This field is required.' }}
                    name={`phones.${index}.customType`}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        required
                        inputRef={field.ref}
                        inputProps={{
                          id: `phones-customType-${index}`,
                        }}
                        InputLabelProps={{
                          htmlFor: `phones-customType-${index}`,
                        }}
                        disabled={disabled}
                        name="phones"
                        label="Custom Type"
                        error={!!phonesErrors?.[index]?.customType}
                        helperText={
                          phonesErrors?.[index]?.customType
                            ? 'This field is required.'
                            : ''
                        }
                        sx={{
                          minWidth: 225,
                        }}
                      />
                    )}
                  />
                )}
                <Controller
                  rules={{ required: 'This field is required.' }}
                  name={`phones.${index}.value`}
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      required
                      name="phones"
                      inputRef={field.ref}
                      error={!!phonesErrors?.[index]?.value}
                      helperText={
                        phonesErrors?.[index]?.value
                          ? 'This field is required.'
                          : ''
                      }
                      disabled={disabled}
                      autoComplete="off"
                      label="Phone Number"
                      sx={{
                        minWidth: 225,
                      }}
                    />
                  )}
                />
              </Stack>

              <SharedProfileInformationItemActions
                index={index}
                totalCount={phonesCount}
                disabled={disabled}
                onMoveDown={() => move(index, index + 1)}
                onMoveUp={() => move(index, index - 1)}
                onDelete={() => remove(index)}
              />
            </Stack>
            {index !== fields.length - 1 && <Divider />}
          </Box>
        );
      })}
    </SharedProfileInformationAccordion>
  );
}
